'use client';

import Icon from '@/components/ui/AppIcon';

interface MRVValidationProps {
  currentLanguage: string;
}

const MRVValidation = ({ currentLanguage }: MRVValidationProps) => {
  const mrvSteps = [
    {
      phase: 'Monitoring',
      icon: 'MagnifyingGlassIcon',
      description: 'Continuous data collection from multiple sources',
      color: 'primary',
      activities: [
        'Automated sensor readings',
        'Manual data entry validation',
        'Third-party data integration',
        'Quality control checks',
      ],
      accuracy: '99.5%',
    },
    {
      phase: 'Reporting',
      icon: 'DocumentTextIcon',
      description: 'Standardized emission reporting and documentation',
      color: 'info',
      activities: [
        'Automated report generation',
        'Multi-framework compliance',
        'Audit trail maintenance',
        'Stakeholder dashboards',
      ],
      accuracy: '98.8%',
    },
    {
      phase: 'Verification',
      icon: 'ShieldCheckIcon',
      description: 'AI-powered accuracy validation and third-party audit support',
      color: 'success',
      activities: [
        'AI anomaly detection',
        'Cross-reference validation',
        'Auditor collaboration tools',
        'Certification support',
      ],
      accuracy: '99.2%',
    },
  ];

  const validationChecks = [
    {
      check: 'Data Completeness',
      status: 'passed',
      score: 98,
      details: 'All required data points collected for reporting period',
    },
    {
      check: 'Calculation Accuracy',
      status: 'passed',
      score: 99,
      details: 'Emission factors and methodologies verified against standards',
    },
    {
      check: 'Temporal Consistency',
      status: 'passed',
      score: 97,
      details: 'Data timestamps aligned across all sources',
    },
    {
      check: 'Boundary Alignment',
      status: 'warning',
      score: 92,
      details: 'Minor discrepancies in Scope 3 boundary definitions',
    },
    {
      check: 'Documentation Quality',
      status: 'passed',
      score: 96,
      details: 'Supporting evidence and metadata complete',
    },
    {
      check: 'Audit Readiness',
      status: 'passed',
      score: 95,
      details: 'All documentation prepared for external verification',
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'passed':
        return 'success';
      case 'warning':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'text-secondary';
    }
  };

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-success/10 px-4 py-2 rounded-full mb-4">
            <Icon name="ShieldCheckIcon" size={20} className="text-success" />
            <span className="text-sm font-semibold text-success">MRV Framework</span>
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
            Carbon Accounting MRV Validation
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Monitoring, Reporting, and Verification with AI-powered accuracy checks
          </p>
        </div>

        {/* MRV Process Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {mrvSteps.map((step, index) => (
            <div key={index} className="bg-card border border-border rounded-xl p-6 hover:shadow-natural-lg transition-all duration-300">
              <div className="text-center mb-6">
                <div className={`inline-flex p-4 bg-${step.color}/10 rounded-full mb-4`}>
                  <Icon name={step.icon} size={40} className={`text-${step.color}`} />
                </div>
                <h3 className="font-heading font-bold text-2xl text-text-primary mb-2">{step.phase}</h3>
                <p className="text-sm text-text-secondary">{step.description}</p>
              </div>

              <div className="space-y-3 mb-6">
                {step.activities.map((activity, idx) => (
                  <div key={idx} className="flex items-start space-x-2">
                    <Icon name="CheckCircleIcon" size={16} className={`text-${step.color} mt-0.5 flex-shrink-0`} />
                    <span className="text-sm text-text-primary">{activity}</span>
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Accuracy Rate</span>
                  <span className={`text-lg font-heading font-bold text-${step.color}`}>{step.accuracy}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Validation Dashboard */}
        <div className="bg-card border-2 border-primary rounded-2xl p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="font-heading font-bold text-2xl text-text-primary mb-2">
                Validation Dashboard
              </h3>
              <p className="text-text-secondary">Current reporting period: Q4 2024</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-heading font-bold text-success mb-1">96.5%</div>
              <div className="text-sm text-text-secondary">Overall Score</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {validationChecks.map((check, index) => (
              <div key={index} className="bg-surface border border-border rounded-lg p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3 flex-grow">
                    <Icon
                      name={check.status === 'passed' ? 'CheckCircleIcon' : 'ExclamationTriangleIcon'}
                      size={20}
                      className={`text-${getStatusColor(check.status)} flex-shrink-0`}
                    />
                    <div>
                      <h4 className="font-semibold text-text-primary">{check.check}</h4>
                      <p className="text-xs text-text-secondary mt-1">{check.details}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xl font-heading font-bold text-${getStatusColor(check.status)}`}>
                      {check.score}%
                    </div>
                  </div>
                </div>
                <div className="w-full bg-border rounded-full h-2 overflow-hidden">
                  <div
                    className={`bg-${getStatusColor(check.status)} h-full rounded-full transition-all duration-700`}
                    style={{ width: `${check.score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* AI Insights */}
          <div className="bg-gradient-to-r from-primary/5 to-info/5 border border-primary/20 rounded-xl p-6">
            <div className="flex items-start space-x-3">
              <Icon name="SparklesIcon" size={24} className="text-primary mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-primary mb-2">AI Validation Insights</h4>
                <div className="space-y-2">
                  <p className="text-sm text-text-primary">
                    • Detected minor boundary inconsistencies in Scope 3 calculations - recommend review of supplier emission allocations
                  </p>
                  <p className="text-sm text-text-primary">
                    • All core emission factors verified against latest IPCC guidelines
                  </p>
                  <p className="text-sm text-text-primary">
                    • Data completeness improved by 12% vs. previous quarter
                  </p>
                </div>
                <button className="mt-4 text-sm font-semibold text-primary hover:text-primary/80 transition-colors">
                  View Detailed Analysis →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MRVValidation;